import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Send, Bot, User, Loader as Loader2, Upload, FileText, Clock, Trash2, Pencil, Check, X, ToggleLeft, ToggleRight, BookOpen, Sparkles } from 'lucide-react';

interface Message {
  id: string;
  sender: 'manager' | 'agent';
  content: string;
  rules_created: number;
  created_at: string;
}

interface Rule {
  id: string;
  category: string;
  content: string;
  source: string;
  is_active: boolean;
  created_at: string;
}

const CATEGORIES: Record<string, { label: string; color: string }> = {
  documentos: { label: 'Documentos', color: 'bg-blue-100 text-blue-700' },
  requisitos: { label: 'Requisitos', color: 'bg-emerald-100 text-emerald-700' },
  valores: { label: 'Valores', color: 'bg-amber-100 text-amber-700' },
  conduta: { label: 'Conduta', color: 'bg-rose-100 text-rose-700' },
  processo: { label: 'Processo', color: 'bg-cyan-100 text-cyan-700' },
  restricoes: { label: 'Restricoes', color: 'bg-red-100 text-red-700' },
  geral: { label: 'Geral', color: 'bg-slate-100 text-slate-700' },
};

export default function InstructionsTab() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [loadingRules, setLoadingRules] = useState(true);
  const [editingRule, setEditingRule] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editCategory, setEditCategory] = useState('geral');
  const [filterCategory, setFilterCategory] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { loadMessages(); loadRules(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  async function getToken() {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || '';
  }

  async function callInstruct(body: any) {
    const token = await getToken();
    const resp = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-instruct`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify(body),
      },
    );
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(json?.error || `Erro ${resp.status}`);
    return json;
  }

  async function loadMessages() {
    setLoadingMsgs(true);
    const { data } = await supabase
      .from('ai_instruction_messages')
      .select('id, sender, content, rules_created, created_at')
      .order('created_at', { ascending: true })
      .limit(100);
    setMessages((data ?? []) as Message[]);
    setLoadingMsgs(false);
  }

  async function loadRules() {
    setLoadingRules(true);
    const { data } = await supabase
      .from('ai_unit_rules')
      .select('id, category, content, source, is_active, created_at')
      .order('created_at', { ascending: false })
      .limit(200);
    setRules((data ?? []) as Rule[]);
    setLoadingRules(false);
  }

  async function sendInstruction() {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);

    const tempMsg: Message = {
      id: 'temp-' + Date.now(),
      sender: 'manager',
      content: text,
      rules_created: 0,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMsg]);

    try {
      const json = await callInstruct({ action: 'send_instruction', content: text });
      if (json.reply) {
        setMessages((prev) => [...prev, json.reply as Message]);
      }
      if (json.rules_saved?.length > 0) {
        setRules((prev) => [...json.rules_saved, ...prev]);
      }
    } catch (err) {
      const errMsg: Message = {
        id: 'err-' + Date.now(),
        sender: 'agent',
        content: `[Erro: ${(err as Error).message}]`,
        rules_created: 0,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errMsg]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);

    try {
      let extractedText = '';
      if (file.type === 'text/plain' || file.name.endsWith('.txt') || file.name.endsWith('.csv')) {
        extractedText = await file.text();
      } else if (file.type === 'application/json' || file.name.endsWith('.json')) {
        extractedText = await file.text();
      } else {
        extractedText = await file.text();
      }

      if (!extractedText.trim()) {
        alert('Nao foi possivel extrair texto do arquivo. Tente colar o conteudo diretamente no chat.');
        return;
      }

      const tempMsg: Message = {
        id: 'temp-doc-' + Date.now(),
        sender: 'manager',
        content: `[Documento: ${file.name}]\n\n${extractedText.slice(0, 500)}${extractedText.length > 500 ? '...' : ''}`,
        rules_created: 0,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, tempMsg]);
      setSending(true);

      const json = await callInstruct({
        action: 'send_instruction',
        content: `Extraia todas as regras, requisitos e politicas deste documento:`,
        document_text: extractedText,
        document_name: file.name,
        file_type: file.type || 'text/plain',
      });

      if (json.reply) setMessages((prev) => [...prev, json.reply as Message]);
      if (json.rules_saved?.length > 0) setRules((prev) => [...json.rules_saved, ...prev]);
    } catch (err) {
      alert(`Erro ao processar documento: ${(err as Error).message}`);
    } finally {
      setUploadingDoc(false);
      setSending(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function toggleRule(ruleId: string, currentActive: boolean) {
    const newActive = !currentActive;
    setRules((prev) => prev.map((r) => r.id === ruleId ? { ...r, is_active: newActive } : r));
    try {
      await callInstruct({ action: 'toggle_rule', rule_id: ruleId, is_active: newActive });
    } catch {
      setRules((prev) => prev.map((r) => r.id === ruleId ? { ...r, is_active: currentActive } : r));
    }
  }

  async function deleteRule(ruleId: string) {
    if (!confirm('Apagar esta regra permanentemente?')) return;
    setRules((prev) => prev.filter((r) => r.id !== ruleId));
    try {
      await callInstruct({ action: 'delete_rule', rule_id: ruleId });
    } catch { loadRules(); }
  }

  async function saveRuleEdit(ruleId: string) {
    if (!editContent.trim()) return;
    try {
      await callInstruct({ action: 'update_rule', rule_id: ruleId, content: editContent.trim(), category: editCategory });
      setRules((prev) => prev.map((r) => r.id === ruleId ? { ...r, content: editContent.trim(), category: editCategory } : r));
      setEditingRule(null);
    } catch (err) {
      alert((err as Error).message);
    }
  }

  function formatTime(iso: string) {
    return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  const filteredRules = filterCategory ? rules.filter((r) => r.category === filterCategory) : rules;
  const activeRulesCount = rules.filter((r) => r.is_active).length;

  return (
    <div className="flex h-[650px] gap-4">
      {/* Chat area */}
      <div className="flex flex-1 flex-col rounded-xl border border-slate-200 bg-white">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-emerald-100 p-1.5">
              <BookOpen className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Ensinar o Agente</h3>
              <p className="text-[10px] text-slate-500">{activeRulesCount} regras ativas</p>
            </div>
          </div>
          <button
            onClick={() => setShowRules(!showRules)}
            className={
              'rounded-lg px-3 py-1.5 text-xs font-medium transition ' +
              (showRules ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
            }
          >
            {showRules ? 'Ocultar regras' : 'Ver regras'}
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loadingMsgs ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center gap-3 px-8">
              <div className="rounded-2xl bg-emerald-50 p-4">
                <Sparkles className="h-10 w-10 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800">Ensine suas regras ao agente</h3>
              <p className="max-w-md text-sm text-slate-500">
                Digite instrucoes como: "Sempre peca RG e comprovante de residencia",
                "Valor minimo de 500 reais", ou anexe um documento com suas politicas.
                O agente vai absorver as regras e aplica-las em todas as conversas.
              </p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={'flex ' + (msg.sender === 'manager' ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={
                    'relative max-w-[80%] rounded-2xl px-4 py-2.5 ' +
                    (msg.sender === 'manager'
                      ? 'bg-emerald-600 text-white rounded-br-md'
                      : 'bg-slate-100 text-slate-800 rounded-bl-md')
                  }
                >
                  <div className="flex items-start gap-2">
                    {msg.sender === 'agent' && <Bot className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />}
                    <div className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</div>
                    {msg.sender === 'manager' && <User className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-200" />}
                  </div>
                  <div className={'mt-1 flex items-center gap-2 text-[10px] ' + (msg.sender === 'manager' ? 'text-emerald-200 justify-end' : 'text-slate-400')}>
                    <Clock className="h-2.5 w-2.5" />
                    {formatTime(msg.created_at)}
                    {msg.sender === 'agent' && msg.rules_created > 0 && (
                      <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-emerald-700 font-medium">
                        <Check className="h-2.5 w-2.5" /> {msg.rules_created} {msg.rules_created === 1 ? 'regra salva' : 'regras salvas'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 rounded-2xl bg-slate-100 px-4 py-3 rounded-bl-md">
                <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
                <span className="text-xs text-slate-500">Processando instrucao...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-slate-200 px-4 py-3">
          <div className="flex items-end gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".txt,.csv,.json,.md,.pdf"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || uploadingDoc}
              className="rounded-xl border border-slate-300 bg-white p-2.5 text-slate-600 transition hover:bg-slate-50 hover:border-slate-400 disabled:opacity-50"
              title="Anexar documento"
            >
              {uploadingDoc ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            </button>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendInstruction();
                }
              }}
              placeholder="Ensine uma regra, requisito ou politica... (Enter para enviar)"
              disabled={sending}
              rows={1}
              className="flex-1 resize-none rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-sm placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
              style={{ minHeight: '42px', maxHeight: '120px' }}
              onInput={(e) => {
                const t = e.currentTarget;
                t.style.height = '42px';
                t.style.height = Math.min(t.scrollHeight, 120) + 'px';
              }}
            />
            <button
              onClick={sendInstruction}
              disabled={sending || !input.trim()}
              className="rounded-xl bg-emerald-600 p-2.5 text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
          <p className="mt-1.5 text-[10px] text-slate-400">
            Voce pode digitar varias regras de uma vez ou colar listas inteiras. Tambem pode anexar documentos .txt, .csv, .md
          </p>
        </div>
      </div>

      {/* Rules panel */}
      {showRules && (
        <div className="flex w-80 flex-shrink-0 flex-col rounded-xl border border-slate-200 bg-slate-50">
          <div className="border-b border-slate-200 px-3 py-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-700">Regras Aprendidas</h3>
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                {activeRulesCount} ativas
              </span>
            </div>
            {/* Category filter */}
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setFilterCategory(null)}
                className={
                  'rounded-md px-2 py-0.5 text-[10px] font-medium transition ' +
                  (!filterCategory ? 'bg-slate-700 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100')
                }
              >
                Todas
              </button>
              {Object.entries(CATEGORIES).map(([key, { label, color }]) => {
                const count = rules.filter((r) => r.category === key).length;
                if (count === 0) return null;
                return (
                  <button
                    key={key}
                    onClick={() => setFilterCategory(filterCategory === key ? null : key)}
                    className={
                      'rounded-md px-2 py-0.5 text-[10px] font-medium transition ' +
                      (filterCategory === key ? 'bg-slate-700 text-white' : `${color} hover:opacity-80`)
                    }
                  >
                    {label} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {loadingRules ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : filteredRules.length === 0 ? (
              <div className="px-3 py-8 text-center text-xs text-slate-400">
                {filterCategory ? 'Nenhuma regra nessa categoria.' : 'Nenhuma regra ainda. Ensine algo ao agente!'}
              </div>
            ) : (
              filteredRules.map((rule) => (
                <div
                  key={rule.id}
                  className={
                    'group rounded-lg border px-3 py-2 transition ' +
                    (rule.is_active
                      ? 'bg-white border-slate-200'
                      : 'bg-slate-100 border-slate-200 opacity-60')
                  }
                >
                  {editingRule === rule.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full rounded-md border border-slate-300 p-2 text-xs focus:border-emerald-500 focus:outline-none"
                        rows={3}
                      />
                      <select
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs"
                      >
                        {Object.entries(CATEGORIES).map(([key, { label }]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                      <div className="flex gap-1">
                        <button onClick={() => saveRuleEdit(rule.id)} className="rounded-md bg-emerald-600 px-2 py-1 text-[10px] text-white hover:bg-emerald-700">
                          <Check className="h-3 w-3" />
                        </button>
                        <button onClick={() => setEditingRule(null)} className="rounded-md border border-slate-200 px-2 py-1 text-[10px] text-slate-600 hover:bg-slate-50">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs leading-relaxed text-slate-700">{rule.content}</p>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
                          <button
                            onClick={() => { setEditingRule(rule.id); setEditContent(rule.content); setEditCategory(rule.category); }}
                            className="rounded p-1 hover:bg-slate-100"
                            title="Editar"
                          >
                            <Pencil className="h-3 w-3 text-slate-500" />
                          </button>
                          <button
                            onClick={() => toggleRule(rule.id, rule.is_active)}
                            className="rounded p-1 hover:bg-slate-100"
                            title={rule.is_active ? 'Desativar' : 'Ativar'}
                          >
                            {rule.is_active
                              ? <ToggleRight className="h-3.5 w-3.5 text-emerald-500" />
                              : <ToggleLeft className="h-3.5 w-3.5 text-slate-400" />
                            }
                          </button>
                          <button
                            onClick={() => deleteRule(rule.id)}
                            className="rounded p-1 hover:bg-red-50"
                            title="Apagar"
                          >
                            <Trash2 className="h-3 w-3 text-red-400" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-medium ${CATEGORIES[rule.category]?.color || CATEGORIES.geral.color}`}>
                          {CATEGORIES[rule.category]?.label || rule.category}
                        </span>
                        {rule.source === 'document' && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] text-slate-400">
                            <FileText className="h-2.5 w-2.5" /> doc
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
